'use client';

import Link from 'next/link';
import { getRepId, getRepFullName, getRepTitle, getRepState, getRepParty, getRepLink } from '@/app/lib/reps';

interface RepCardProps {
  rep: any;
  // 'result': searchable card with an Add button; 'cart': selected card with a remove ✕
  variant: 'result' | 'cart';
  inCart?: boolean;
  onAdd?: (rep: any) => void;
  onRemove?: (id: string) => void;
  fallbackId?: string;
}

export default function RepCard({ rep, variant, inCart = false, onAdd, onRemove, fallbackId }: RepCardProps) {
  const id = getRepId(rep) || fallbackId || '';
  const fullName = getRepFullName(rep);
  const stateAbbr = getRepState(rep);
  const roleLabel = getRepTitle(rep);
  const role = stateAbbr ? `${roleLabel} · ${stateAbbr}` : roleLabel;
  const party = getRepParty(rep);
  const partyColor = party === 'Democrat' || party === 'Democratic' ? 'text-blue-600' : party === 'Republican' ? 'text-red-600' : 'text-slate-500';
  const repLink = getRepLink(rep);

  const nameEl = repLink
    ? <Link href={repLink} target="_blank" className="hover:text-amber-700 transition-colors">{fullName}</Link>
    : fullName;

  if (variant === 'cart') {
    return (
      <div className="relative p-5 rounded-2xl border-2 border-amber-500 bg-white shadow-xl shadow-amber-900/20">
        <button
          onClick={() => onRemove?.(id)}
          className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 text-xs font-bold flex items-center justify-center transition-colors"
          title="Remove"
        >
          ✕
        </button>
        <div className="pr-6">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{role}</p>
          <h3 className="text-lg font-bold text-slate-900 leading-tight">{nameEl}</h3>
          <p className={`text-sm font-medium mt-1 ${partyColor}`}>{party}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative p-5 rounded-2xl border-2 bg-[#fafaf7] border-slate-200 shadow">
      <div className="pr-6">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{role}</p>
        <h3 className="text-lg font-bold text-slate-900 leading-tight">{nameEl}</h3>
        <p className={`text-sm font-medium mt-1 ${partyColor}`}>{party}</p>
      </div>
      <button
        onClick={() => onAdd?.(rep)}
        disabled={inCart}
        className={`mt-3 w-full py-2 rounded-xl text-sm font-bold transition-all ${
          inCart
            ? 'bg-green-100 text-green-700 cursor-default'
            : 'bg-amber-500 hover:bg-amber-600 text-white active:scale-95'
        }`}
      >
        {inCart ? '✓ Added' : 'Add to List →'}
      </button>
    </div>
  );
}
